import { createNavigation } from "next-intl/navigation";
import { defineRouting } from "next-intl/routing";
export const defaultLocale = "fi";

export type LinkHrefProps = {
  pathname: string;
  params: Record<string, string>;
};

export type NavItem = {
  label: {
    fi: string;
    en: string;
  };
  pathnames: {
    fi: string;
    en: string;
  };
  icon?: string;
  menu: string[] | "main" | "user" | "footer";
  superAdminOnly?: boolean;
};

export const navItems: NavItem[] = [
  {
    label: {
      fi: "Etusivu",
      en: "Home",
    },
    pathnames: {
      fi: "/",
      en: "/",
    },
    icon: "",
    menu: ["main"],
  },

  {
    label: {
      fi: "Kirjaudu",
      en: "Log In",
    },
    pathnames: {
      fi: "/kirjaudu",
      en: "/login",
    },
    icon: "",
    menu: [],
  },
  {
    label: {
      fi: "Rekisteröidy",
      en: "Register",
    },
    pathnames: {
      fi: "/rekisteroidy",
      en: "/register",
    },
    icon: "",
    menu: [],
  },
  {
    label: {
      fi: "Tietosuojakäytäntö",
      en: "Privacy Policy",
    },
    pathnames: {
      fi: "/tietosuojakaytanto",
      en: "/privacy",
    },
    icon: "",
    menu: ["footer"],
  },
  {
    label: {
      fi: "Turnaukset",
      en: "Tournaments",
    },
    pathnames: {
      fi: "/turnaukset",
      en: "/tournaments",
    },
    icon: "",
    menu: [],
  },
  {
    label: {
      fi: "Turnausarkisto",
      en: "Tournament archive",
    },
    pathnames: {
      fi: "/arkisto",
      en: "/archive",
    },
    icon: "",
    menu: [],
  },
  {
    label: {
      fi: "Miten Tornihaaste toimii?",
      en: "How does Tower Challenger work?",
    },
    pathnames: {
      fi: "/tietoa",
      en: "/how-to",
    },
    icon: "",
    menu: ["main"],
  },
  {
    label: {
      fi: "Luo turnaus",
      en: "Create Tournament",
    },
    pathnames: {
      fi: "/aloita",
      en: "/start",
    },
    icon: "",
    menu: ["main"],
  },
  {
    label: {
      fi: "Profiili",
      en: "Profile",
    },
    pathnames: {
      fi: "/profiili",
      en: "/profile",
    },
    icon: "solar:user-bold",
    menu: ["user"],
  },
  /* TOURNAMENT SPECIFIC */
  {
    label: {
      fi: "Torni",
      en: "Tower",
    },
    pathnames: {
      fi: "/turnaukset/[slug]",
      en: "/tournaments/[slug]",
    },
    icon: "game-icons:stone-tower",
    menu: ["tournament"],
  },
  {
    label: {
      fi: "Ottelut",
      en: "Matches",
    },
    pathnames: {
      fi: "/turnaukset/[slug]/ottelut",
      en: "/tournaments/[slug]/matches",
    },
    icon: "solar:calendar-linear",
    menu: ["tournament"],
  },
  {
    label: {
      fi: "Keskustelu",
      en: "Chat",
    },
    pathnames: {
      fi: "/turnaukset/[slug]/keskustelu",
      en: "/tournaments/[slug]/chat",
    },
    icon: "solar:chat-round-dots-linear",
    menu: ["tournament"],
  },
  // Team chat route — private chat for the user's team in this tournament.
  // Not in any menu; surfaced via the chat hub layout's segmented nav.
  {
    label: {
      fi: "Joukkuekeskustelu",
      en: "Team Chat",
    },
    pathnames: {
      fi: "/turnaukset/[slug]/keskustelu/joukkue",
      en: "/tournaments/[slug]/chat/team",
    },
    icon: "solar:users-group-rounded-linear",
    menu: [],
  },
  // Match chat route — not in any menu (TournamentNavbar injects it
  // dynamically when the user has an active match chat). Registered here so
  // next-intl routing resolves the pathname template. Sits under /chat/match/
  // so that /chat/team and /chat/match/[matchId] don't collide on the same
  // dynamic segment.
  {
    label: {
      fi: "Ottelukeskustelu",
      en: "Match Chat",
    },
    pathnames: {
      fi: "/turnaukset/[slug]/keskustelu/ottelu/[matchId]",
      en: "/tournaments/[slug]/chat/match/[matchId]",
    },
    icon: "game-icons:crossed-swords",
    menu: [],
  },
  // Legacy /feed route — kept registered so the redirect page resolves via next-intl.
  // Not included in any menu.
  {
    label: {
      fi: "Tapahtumat",
      en: "Feed",
    },
    pathnames: {
      fi: "/turnaukset/[slug]/tapahtumat",
      en: "/tournaments/[slug]/feed",
    },
    icon: "",
    menu: [],
  },
  {
    label: {
      fi: "Tietoja",
      en: "About",
    },
    pathnames: {
      fi: "/turnaukset/[slug]/tervetuloa",
      en: "/tournaments/[slug]/about",
    },
    icon: "solar:info-circle-linear",
    menu: ["tournament"],
  },
  {
    label: {
      fi: "Oma joukkue",
      en: "My Team",
    },
    pathnames: {
      fi: "/turnaukset/[slug]/oma-joukkue",
      en: "/tournaments/[slug]/my-team",
    },
    icon: "solar:users-group-rounded-linear",
    menu: ["tournament"],
  },
  {
    label: {
      fi: "Vapaat pelaajat",
      en: "Player Pool",
    },
    pathnames: {
      fi: "/turnaukset/[slug]/vapaat-pelaajat",
      en: "/tournaments/[slug]/pool",
    },
    icon: "solar:hand-shake-linear",
    menu: ["tournament"],
  },
  {
    label: {
      fi: "Tilastot",
      en: "Statistics",
    },
    pathnames: {
      fi: "/turnaukset/[slug]/tilastot",
      en: "/tournaments/[slug]/statistics",
    },
    icon: "lucide:bar-chart-3",
    menu: ["tournament"],
  },
  {
    label: {
      fi: "Ota yhteyttä",
      en: "Contact",
    },
    pathnames: {
      fi: "/turnaukset/[slug]/ota-yhteytta",
      en: "/tournaments/[slug]/contact",
    },
    icon: "solar:letter-linear",
    menu: ["tournament"],
  },

  // Post-tournament survey — linked from the feedback email, so it lives
  // outside the tournament nav and is never listed in a menu.
  {
    label: {
      fi: "Turnauskysely",
      en: "Tournament survey",
    },
    pathnames: {
      fi: "/kysely/[slug]",
      en: "/survey/[slug]",
    },
    icon: "solar:clipboard-text-linear",
    menu: [],
  },

  // ============================================================================
  /* ADMIN */
  // ============================================================================

  /* ORGANIZER SPECIFIC */
  {
    label: {
      fi: "Pelipaikat",
      en: "Venues",
    },
    pathnames: {
      fi: "/admin/[organizerSlug]/pelipaikat",
      en: "/admin/[organizerSlug]/locations",
    },
    icon: "majesticons:map-marker",
    menu: ["admin"],
  },
  {
    label: {
      fi: "Järjestäjän viestit",
      en: "Organizer Messages",
    },
    pathnames: {
      fi: "/admin/[organizerSlug]/viestit",
      en: "/admin/[organizerSlug]/messages",
    },
    icon: "majesticons:megaphone",
    menu: ["admin"],
  },

  /* SUPER-ADMIN ONLY */
  {
    label: {
      fi: "Järjestäjät",
      en: "Organizers",
    },
    pathnames: {
      fi: "/admin/super-admin/jarjestajat",
      en: "/admin/super-admin/organizers",
    },
    icon: "majesticons:users-line",
    menu: ["admin-super"],
    superAdminOnly: true,
  },

  {
    label: {
      fi: "Kyselytulokset",
      en: "Survey Results",
    },
    pathnames: {
      fi: "/admin/super-admin/kyselytulokset",
      en: "/admin/super-admin/survey-results",
    },
    icon: "solar:clipboard-text-linear",
    menu: ["admin-super"],
    superAdminOnly: true,
  },

  {
    label: {
      fi: "Sovelluksen viestit",
      en: "App Messages",
    },
    pathnames: {
      fi: "/admin/super-admin/sovellusilmoitukset",
      en: "/admin/super-admin/application-notifications",
    },
    icon: "majesticons:megaphone",
    menu: ["admin-super"],
    superAdminOnly: true,
  },

  /* TOURNAMENT SPECIFIC */
  {
    label: {
      fi: "Torni",
      en: "Tower",
    },
    pathnames: {
      fi: "/admin/[organizerSlug]/turnaukset/[slug]",
      en: "/admin/[organizerSlug]/tournaments/[slug]",
    },
    icon: "game-icons:stone-tower",
    menu: ["admin-tournament"],
  },
  {
    label: {
      fi: "Ottelut",
      en: "Matches",
    },
    pathnames: {
      fi: "/admin/[organizerSlug]/turnaukset/[slug]/ottelut",
      en: "/admin/[organizerSlug]/tournaments/[slug]/matches",
    },
    icon: "game-icons:crossed-swords",
    menu: ["admin-tournament"],
  },
  {
    label: {
      fi: "Pelaajat",
      en: "Players",
    },
    pathnames: {
      fi: "/admin/[organizerSlug]/turnaukset/[slug]/pelaajat",
      en: "/admin/[organizerSlug]/tournaments/[slug]/players",
    },
    icon: "ph:users-four-fill",
    menu: ["admin-tournament"],
  },
  {
    label: {
      fi: "Turnausviestintä",
      en: "Tournament Messages",
    },
    pathnames: {
      fi: "/admin/[organizerSlug]/turnaukset/[slug]/ilmoitukset",
      en: "/admin/[organizerSlug]/tournaments/[slug]/notifications",
    },
    icon: "solar:bell-linear",
    menu: ["admin-tournament"],
  },
  {
    label: {
      fi: "Kyselytulokset",
      en: "Survey Results",
    },
    pathnames: {
      fi: "/admin/[organizerSlug]/turnaukset/[slug]/kyselytulokset",
      en: "/admin/[organizerSlug]/tournaments/[slug]/survey-results",
    },
    icon: "solar:clipboard-text-linear",
    menu: ["admin-tournament"],
  },
  {
    label: {
      fi: "Asetukset",
      en: "Settings",
    },
    pathnames: {
      fi: "/admin/[organizerSlug]/turnaukset/[slug]/asetukset",
      en: "/admin/[organizerSlug]/tournaments/[slug]/settings",
    },
    icon: "solar:settings-linear",
    menu: ["admin-tournament"],
  },
];

// Helper function to get a navItem by pathname.
export function getNavItem(pathname: string) {
  return navItems.find(
    (item) => item.pathnames.fi === pathname || item.pathnames.en === pathname,
  );
}

// Helper function to get a navItem by pathname and locale.
export const getNavItemByPathname: (
  pathname: string,
  locale: Locale,
) => NavItem | undefined = (pathname: string) => {
  return navItems.find(
    (item) => item.pathnames.fi === pathname || item.pathnames.en === pathname,
  );
};

/**
 * Get menu items by menu name.
 *
 * @param menu
 *   The menu name.
 * @returns
 *   The menu items.
 */
export function getMenuNavItems(menu: string) {
  return navItems.filter((item) => item.menu.includes(menu));
}

// Generate routing.pathnames from navItems.
const pathnames = navItems.reduce(
  (acc, item) => {
    acc[item.pathnames.en] = {
      fi: item.pathnames.fi,
      en: item.pathnames.en,
    };

    return acc;
  },
  {} as Record<string, { fi: string; en: string }>,
);

export const routing = defineRouting({
  locales: ["fi", "en"],
  defaultLocale: defaultLocale,
  localePrefix: "as-needed",
  localeDetection: false,
  pathnames: pathnames,
});

export type Pathnames = keyof typeof routing.pathnames;
export type Locale = (typeof routing.locales)[number];

export const { Link, redirect, usePathname, useRouter, getPathname } =
  createNavigation(routing);

/**
 * Check if a given href matches the current pathname.
 *
 * When using next-intl with the `pathnames` setting, `usePathname()` returns
 * the internal pathname template (e.g., "/tournaments/[slug]/feed") without
 * the locale prefix and with dynamic params not replaced.
 *
 * This function correctly handles comparison by converting both paths to their
 * pathname template form for accurate matching.
 *
 * @param currentPathname - The current pathname from usePathname() (internal template)
 * @param href - The href to compare against (can be resolved or template)
 * @param exact - If true, only match exact paths. If false, match if pathname starts with href
 * @returns true if the paths match
 */
export function isActivePath(
  currentPathname: string,
  href: string,
  exact: boolean = true,
): boolean {
  // Convert href to pathname template if it contains resolved params
  // e.g., "/tournaments/my-slug/feed" -> "/tournaments/[slug]/feed"
  let hrefTemplate = href;

  // Find matching navItem to get the template
  const matchingItem = navItems.find((item) => {
    const fiTemplate = item.pathnames.fi;
    const enTemplate = item.pathnames.en;

    // Check if href matches the template pattern
    const fiRegex = new RegExp(
      "^" + fiTemplate.replace(/\[([^\]]+)\]/g, "([^/]+)") + "$",
    );
    const enRegex = new RegExp(
      "^" + enTemplate.replace(/\[([^\]]+)\]/g, "([^/]+)") + "$",
    );

    return fiRegex.test(href) || enRegex.test(href);
  });

  // Use the English (internal) template for comparison
  if (matchingItem) {
    hrefTemplate = matchingItem.pathnames.en;
  }

  // Normalize paths by removing trailing slashes for comparison
  const normalizedCurrent = currentPathname.replace(/\/$/, "");
  const normalizedHref = hrefTemplate.replace(/\/$/, "");

  if (exact) {
    return normalizedCurrent === normalizedHref;
  }

  // For non-exact matches, check if current path starts with href
  return (
    normalizedCurrent === normalizedHref ||
    normalizedCurrent.startsWith(normalizedHref + "/")
  );
}
